// queryHelper.ts

import { BudgetSections, BudgetExpenseOrIncome } from 'business/constants';
import { useContext, useMemo } from 'react';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import {
    replaceGestionnaireFieldInFilters,
    addScopeToFilter,
    filterOutNullFilterValue,
    getFiltersFromBudgetProposals,
    getExerciceFilter,
    getExpenseOrIncomeFilter,
    replaceFiltersWithGlobalOnes,
} from 'routes/BudgetModule/helpers/query';
import { useGetScopes } from 'routes/BudgetModule/shared/useGetScopes';
import { useGetUserScopes } from 'routes/BudgetModule/shared/useGetUserScopes';
import _, { Dictionary } from 'lodash';
import { UserScopesT, ScopeT } from 'business/definitions';
import { isCustomBudgetProposal } from 'routes/BudgetModule/components/BudgetProposal/Resource/hooks/budgetProposal';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';

type FiltersSettingsByExpInc = {
    budgetProposals: BudgetProposalT[];
    extraFilters: Pick<SentenceFilterT, 'value' | 'field' | 'operation'>[];
    section?: BudgetSections;
    exercice: number;
    userScopes: UserScopesT;
    scopes: ScopeT[];
};

type FilterSettingsBySection = {
    budgetProposals: Dictionary<BudgetProposalT[]> | BudgetProposalT[];
    filters: SentenceFilterWithScopesT[];
    year: number;
};

export type FiltersByExpInc = Record<BudgetExpenseOrIncome, QueryFilterT[]>;
export type FiltersBySectionByExpInc = Record<BudgetSections, FiltersByExpInc>;

const BUDGET_SECTIONS = [BudgetSections.INVESTMENT, BudgetSections.OPERATING];

const buildAnalyseFilters = ({
    initialFilters,
    budgetProposals,
    expenseOrIncome,
    userScopes,
    scopes,
    exercice,
    section,
}: {
    initialFilters: QueryFilterT[];
    budgetProposals: BudgetProposalT[];
    expenseOrIncome: BudgetExpenseOrIncome;
    userScopes: UserScopesT;
    scopes: ScopeT[];
    exercice: number;
    section?: BudgetSections;
}): QueryFilterT[] => {
    const requestBy = budgetProposals[0]?.by;
    const exerciceRange = 3;
    const allFilters = filterOutNullFilterValue([
        ...getFiltersFromBudgetProposals(budgetProposals, section),
        ...getExerciceFilter(exercice, true, false, exerciceRange),
        ...getExpenseOrIncomeFilter(expenseOrIncome),
        ...addScopeToFilter(requestBy, userScopes, scopes), // Move this into backend ?
    ]);
    return replaceFiltersWithGlobalOnes(allFilters, initialFilters);
};

/**
 * Creates filters for both expense and income based on the provided settings.
 *
 * @param budgetProposals - The budget proposals to include in the queries.
 * @param extraFilters - filters to apply to the queries.
 * @param section - The budget section for which the queries are created.
 * @param exercice - The exercise year for which the queries are created.
 * @param userScopes - The user scopes to apply in the queries.
 * @param scopes - The list of scopes.
 */
const createExpIncFilters = ({
    budgetProposals,
    extraFilters,
    section,
    exercice,
    userScopes,
    scopes,
}: FiltersSettingsByExpInc): FiltersByExpInc => {
    const filters: FiltersByExpInc = {} as FiltersByExpInc;
    [BudgetExpenseOrIncome.EXPENSE, BudgetExpenseOrIncome.INCOME].forEach(
        (expOrInc) => {
            filters[expOrInc] = buildAnalyseFilters({
                initialFilters: extraFilters,
                budgetProposals,
                expenseOrIncome: expOrInc,
                userScopes,
                scopes,
                exercice,
                section,
            });
        },
    );

    return filters;
};

/**
 * Build filters ventilated by section then by expenseOrIncome.
 *
 * @param budgetProposalsBySection - Budget proposals grouped by section or not if in case of sifac.
 * @param filters - The filters we got from the budgetFilters.
 * @param year - The exercice year for which the queries are being created.
 */
export const useCreateFilters = ({
    budgetProposals,
    filters,
    year,
}: FilterSettingsBySection): FiltersBySectionByExpInc | FiltersByExpInc => {
    const extraFilters = useMemo(
        () =>
            replaceGestionnaireFieldInFilters(
                filters.map((filter) =>
                    _.pick(filter, ['field', 'operation', 'value']),
                ),
            ),
        [filters],
    );
    const userScopes = useGetUserScopes();
    const scopes = useGetScopes();
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);

    const finalPivotTableQueries = useMemo(() => {
        if (!isSchool) {
            const filtersBySection = {} as FiltersBySectionByExpInc;
            BUDGET_SECTIONS.forEach((section) => {
                const sectionProposals = (
                    budgetProposals as Record<BudgetSections, BudgetProposalT[]>
                )[section];
                filtersBySection[section] = createExpIncFilters({
                    budgetProposals: sectionProposals,
                    extraFilters,
                    section,
                    exercice: year,
                    userScopes,
                    scopes,
                });
            });
            return filtersBySection;
        } else {
            // SIFAC
            const filtersByExpInc = createExpIncFilters({
                budgetProposals: budgetProposals as BudgetProposalT[],
                extraFilters,
                exercice: year,
                userScopes,
                scopes,
            });

            return filtersByExpInc;
        }
    }, [budgetProposals, extraFilters, year]);
    return finalPivotTableQueries;
};
